import { ProductInterface } from '../../services/exports/Interfaces';
import CustomCarousel, { Item } from './CustomCarousel';
import useScreenType from '../../hooks/utility/useScreenType';
import { useTranslation } from 'react-i18next';
import SeoResource from '../../services/resources/SeoResource';
import { ReactElement, useMemo } from 'react';

interface Props {
  products: ProductInterface[] | null;
  hideTitle?: boolean;
}

const ProductsCarousel = (props: Props): JSX.Element | undefined => {
  const { products, hideTitle } = props;

  const { t } = useTranslation(null, { keyPrefix: 'Components:Carousel:ProductsCarousel' });

  const { isDesktop } = useScreenType();

  const ProductWithImage = ({
    item: product,
    itemHeight,
  }: {
    item: ProductInterface;
    itemHeight: number;
  }): ReactElement => {
    const nameHeight = 40;

    return (
      <div className="rounded-[8px] border overflow-hidden">
        <div style={{ height: itemHeight - nameHeight }}>
          <img
            src={product?.thumbnail_url}
            alt={SeoResource.toAltText(product?.name)}
            className="h-full w-full object-cover"
          />
        </div>
        <div className="flex w-full justify-center items-center px-2xs" style={{ height: nameHeight }}>
          <p className="text-xs text-center h-fit line-clamp-1 break-words">{product?.name}</p>
        </div>
      </div>
    );
  };

  const ProductWithoutImage = ({ item: product }: { item: ProductInterface }): ReactElement => {
    return (
      <div className="h-full w-full flex flex-col justify-center items-center px-xs rounded-[8px] border overflow-hidden">
        <p className="text-xs text-center line-clamp-1 break-words">{product?.name}</p>
        {product?.description && (
          <p className="text-xs text-center text-gray-700 line-clamp-2 break-words mt-2xs">{product?.description}</p>
        )}
      </div>
    );
  };

  const items = useMemo<Item[]>(
    () =>
      products?.map((item) => ({
        data: item,
        render: (index, itemHeight) =>
          item.thumbnail_url ? (
            <ProductWithImage item={item} itemHeight={itemHeight} />
          ) : (
            <ProductWithoutImage item={item} />
          ),
      })),
    [products],
  );

  return (
    <>
      {!hideTitle && (
        <div className="mb-xs">
          <p className="text-sm">{t('applies_to_articles')}</p>
        </div>
      )}
      <CustomCarousel items={items} itemsPerSlide={isDesktop ? 3 : 2} />
    </>
  );
};

export default ProductsCarousel;
